import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import { toast } from "react-toastify";
import { sendGeneralEmail } from "../api/tenantApi";
import { createCompanyFromPlatform, startUnderwritingFlow } from "../api/companyApi";

export const BILL_TENANT_ID = "vuffR0CbVJmdSwxprNIb";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const asEmail = (value) => {
  if (typeof value === "string" && emailPattern.test(value.trim())) return value.trim();
  if (value && typeof value === "object" && emailPattern.test(String(value.email || "").trim())) {
    return String(value.email).trim();
  }
  return "";
};

const money = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const numberValue = (value) => {
  if (value === null || value === undefined || value === "" || value === "-") return 0;
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const cleanText = (value) => String(value || "").trim().replace(/[.\s]+$/, "");

const stringList = (value) => {
  if (!value) return [];
  const values = Array.isArray(value) ? value : String(value).split(/\n|;|•/);
  return values
    .map((item) => {
      if (typeof item === "string") return cleanText(item);
      if (!item || typeof item !== "object") return "";
      return cleanText(item.label || item.title || item.requirement || item.description || item.recommendation);
    })
    .filter(Boolean);
};

const evidenceList = (summary, rationale) => {
  const saved =
    summary["Evidence Required"] ||
    summary["Required Evidence"] ||
    summary["Evidence Gaps"] ||
    summary["Evidence Gap"];
  if (saved) return stringList(saved);

  const match = String(rationale || "").match(
    /evidence gaps?\s*:\s*([\s\S]+?)(?:\.\s*(?:manual review|required next step|decision)\b|$)/i
  );
  return match?.[1]
    ? stringList(match[1])
    : [];
};

const canonicalPathType = (value) => {
  const text = String(value || "").toLowerCase().trim().replace(/[\s-]+/g, "_");
  if (/entity|identity|verify.*borrower|legal_entity/.test(text)) return "entity_verification";
  if (/parent/.test(text)) return "parent_support";
  if (/sponsor/.test(text)) return "sponsor_support";
  if (/spend|settlement|repayment|exposure/.test(text)) return "spend_proof";
  if (/adverse/.test(text)) return "adverse_review";
  if (/legal|compliance|lawsuit|bankruptcy/.test(text)) return "legal_review";
  if (/insurance|insured/.test(text)) return "credit_insurance";
  if (/collateral|security|secured/.test(text)) return "collateral_support";
  if (/financial|liquidity|funding|capacity|limit_gap/.test(text)) return "limit_gap";
  return text;
};

const pathType = ({ explicitType, savedPath, rationale, currentRecommendedLimit }) => {
  if (explicitType) return canonicalPathType(explicitType);
  const source = `${savedPath || ""} ${rationale || ""}`.toLowerCase();
  if (/parent|sponsor|guarantee|guarantor/.test(source)) return "parent_support";
  if (/credit insurance|insured|insurance/.test(source)) return "credit_insurance";
  if (/collateral|security interest|secured/.test(source)) return "collateral_support";
  return currentRecommendedLimit > 0 ? "limit_gap" : "manual_evaluation";
};

const evidenceCategory = (value) => {
  const text = String(value || "").toLowerCase();
  if (/certificate|incorporation|good standing|registry|legal entity|address|identity/.test(text)) return "identity";
  if (/parent.*financial|sponsor.*financial|guarantor.*financial|standalone financial|cash balance/.test(text)) return "parent_financial";
  if (/financial statement|balance sheet|income statement|cash flow|liquidity|management account|audit/.test(text)) return "financial";
  if (/debt|obligation|leverage|borrowing/.test(text)) return "debt";
  if (/guarantee|guaranty|comfort letter|cover letter/.test(text)) return "guarantee";
  if (/ownership|signing authority|authorized signatory|board resolution/.test(text)) return "ownership";
  if (/spend|settlement|repayment|payment history|30-day exposure|transaction/.test(text)) return "exposure";
  if (/insurance|policy|binder|coverage/.test(text)) return "insurance";
  if (/collateral|security interest|lien|letter of credit/.test(text)) return "collateral";
  if (/legal|court|lawsuit|bankruptcy|claim|counsel|settlement|disposition/.test(text)) return "legal";
  return "other";
};

const routeCategories = {
  entity_verification: ["identity", "ownership", "parent_financial", "guarantee"],
  parent_support: ["parent_financial", "guarantee", "ownership", "identity"],
  sponsor_support: ["parent_financial", "guarantee", "ownership", "identity"],
  spend_proof: ["exposure", "financial"],
  legal_review: ["legal", "identity"],
  adverse_review: ["legal", "identity"],
  credit_insurance: ["insurance", "exposure"],
  collateral_support: ["collateral", "ownership"],
  limit_gap: ["financial", "debt", "exposure", "identity"],
  manual_evaluation: ["financial", "debt", "exposure", "identity", "legal"],
};

const defaultEvidence = ({ type, existing, parentName }) => {
  const allowed = routeCategories[type] || routeCategories.limit_gap;
  const items = existing.filter((item) => allowed.includes(evidenceCategory(item)));
  const add = (item) => {
    if (!items.some((saved) => saved.toLowerCase() === item.toLowerCase())) items.push(item);
  };

  if (type === "parent_support") {
    add(`${parentName || "Parent/sponsor"} financial statements and current liquidity`);
    add(`Executed, legally enforceable guarantee from ${parentName || "the parent/sponsor"}`);
    add("Evidence of ownership and authorized signing authority");
  } else if (type === "entity_verification") {
    add("Certificate of incorporation or current registry extract for the exact borrower");
    add("Proof of the borrower address and authorized signing authority");
  } else if (type === "spend_proof") {
    add("Recent card spend and settlement history supporting the requested 30-day exposure");
    add("Evidence of timely repayment and projected peak exposure");
  } else if (type === "legal_review" || type === "adverse_review") {
    add("Official disposition of the identified legal or adverse matter");
    add("Counsel letter or court record establishing the current status and financial impact");
  } else if (type === "credit_insurance") {
    add("Credit-insurance binder or policy terms naming the borrower and supported limit");
  } else if (type === "collateral_support") {
    add("Executed collateral or security agreement");
    add("Evidence that the security interest or letter of credit is valid and enforceable");
  } else if (!items.length) {
    add("Current borrower financial statements and liquidity");
    add("Current debt schedule and material obligations");
    add("Evidence supporting the requested 30-day exposure");
  }

  return items.slice(0, 6);
};

const defaultAlternatives = ({ type, parentName, currentRecommendedLimit, requestedLimit }) => {
  if (type === "parent_support") {
    return [
      {
        title: "Direct parent facility",
        recommendation: `Underwrite and execute the facility directly with ${parentName || "the parent/sponsor"}.`,
      },
      {
        title: "Guaranteed structure",
        recommendation: `Keep the operating company as obligor with a verified, enforceable guarantee from ${parentName || "the parent/sponsor"}.`,
      },
      {
        title: "Staged approval",
        recommendation: "Keep the current recommendation and consider an increase only after evidence verification and Risk approval.",
      },
    ];
  }

  return [
    {
      title: "Maintain current limit",
      recommendation: `Keep ${money(currentRecommendedLimit)} until the evidence package is verified.`,
    },
    {
      title: "Staged increase",
      recommendation: `Consider a staged path toward ${money(requestedLimit)}, subject to underwriting and Risk approval.`,
    },
    {
      title: "Risk-transfer structure",
      recommendation: "Consider a partially insured or otherwise risk-mitigated structure if available and approved.",
    },
  ];
};

const alternativeTitle = (text, index) => {
  const value = String(text || "").toLowerCase();
  if (/direct.*parent|facility directly/.test(value)) return "Direct parent facility";
  if (/guarantee|collateral|letter of credit|credit insurance|insured/.test(value)) return "Risk mitigation";
  if (/stage|cycle|step.*increase|after.*clean/.test(value)) return "Staged increase";
  if (/settlement|direct debit|peak exposure|transaction-level/.test(value)) return "Exposure controls";
  if (/maintain|keep.*current/.test(value)) return "Maintain current limit";
  return `Option ${index + 1}`;
};

const normalizeAlternatives = (value, fallback) => {
  if (!Array.isArray(value) || !value.length) return fallback;
  return value
    .map((item, index) => {
      if (typeof item === "string") {
        const recommendation = cleanText(item);
        return { title: alternativeTitle(recommendation, index), recommendation };
      }
      const recommendation = cleanText(item?.recommendation || item?.description || item?.summary);
      return {
        title: cleanText(item?.title || item?.label || alternativeTitle(recommendation, index)),
        recommendation,
      };
    })
    .filter((item) => item.recommendation);
};

const workflowSteps = ({ type, parentName, evidenceRequired, reunderwriteTrigger }) => [
  {
    title: "Collect and verify evidence",
    description: evidenceRequired.length
      ? `${evidenceRequired.length} requirement${evidenceRequired.length === 1 ? "" : "s"} identified for review.`
      : "Complete the required evidence package.",
    status: "ready",
  },
  {
    title: type === "parent_support" ? "Underwrite parent support" : "Refresh underwriting",
    description: type === "parent_support"
      ? `Assess ${parentName || "the parent/sponsor"} and validate the proposed support.`
      : reunderwriteTrigger,
    status: "pending",
  },
  {
    title: "VP Risk decision",
    description: "Approve, decline, or return the requested-limit path after verification.",
    status: "pending",
  },
];

export const resolvePathDetails = (company = {}) => {
  const summary = company.summaryData || {};
  const explicit = summary["Path to Requested Limit Details"];
  const configured = explicit && typeof explicit === "object" && !Array.isArray(explicit)
    ? explicit
    : {};

  const requestedLimit = numberValue(
    configured.requestedLimit ??
    company.requestedCreditLimit ??
    summary["Requested Limit"] ??
    summary["Amount Requested"] ??
    summary["Credit Limit Requested"]
  );
  const currentRecommendedLimit = numberValue(
    configured.currentRecommendedLimit ??
    summary["Recommended Limit"] ??
    company.recommendedLimit
  );
  if (!requestedLimit || currentRecommendedLimit >= requestedLimit) return null;

  const rationale = summary["Underwriting Reason"] || summary.Rationale || "";
  const savedPath = configured.primaryAction || configured.summary || summary["Path to Requested Limit"];
  const parentName =
    configured.parentName ||
    summary["Parent Company"] ||
    company.parentCompanyName ||
    "";
  const type = pathType({
    explicitType: configured.type,
    savedPath,
    rationale,
    currentRecommendedLimit,
  });
  const evidenceRequired = defaultEvidence({
    type,
    existing: [
      ...stringList(configured.evidenceRequired),
      ...evidenceList(summary, rationale),
    ].filter((item, index, values) =>
      values.findIndex((saved) => saved.toLowerCase() === item.toLowerCase()) === index
    ),
    parentName,
  });
  const evidenceAction = evidenceRequired.length
    ? `Provide ${evidenceRequired.join("; ")} and request a manual credit review.`
    : "Provide additional capacity evidence and request a manual credit review.";
  const reunderwriteTrigger =
    configured.reunderwriteTrigger ||
    summary["Re-underwrite Trigger"] ||
    summary["Reunderwrite Trigger"] ||
    (type === "parent_support"
      ? "Re-underwrite after parent financial capacity, ownership, signing authority, and the guarantee are verified."
      : "Re-underwrite after the required evidence is uploaded and verified.");
  const fallbackAlternatives = defaultAlternatives({
    type,
    parentName,
    currentRecommendedLimit,
    requestedLimit,
  });
  const alternativeStructures = normalizeAlternatives(
    configured.alternativeStructures,
    fallbackAlternatives
  );

  return {
    ...configured,
    type,
    summary: configured.summary || savedPath || evidenceAction,
    primaryAction: configured.primaryAction || savedPath || evidenceAction,
    currentArcScore: numberValue(
      configured.currentArcScore ??
      summary["Combined Score"] ??
      summary["ARC Score"]
    ),
    currentRecommendedLimit,
    requestedLimit,
    evidenceRequired,
    evidenceProgress: Array.isArray(configured.evidenceProgress) ? configured.evidenceProgress : [],
    alternativeStructures,
    structureProgress: Array.isArray(configured.structureProgress) ? configured.structureProgress : [],
    satisfiedEvidence: stringList(configured.satisfiedEvidence),
    outstandingEvidence: stringList(configured.outstandingEvidence),
    lastEvidenceAssessment: configured.lastEvidenceAssessment || null,
    reunderwriteTrigger,
    parentName,
    workflowSteps: Array.isArray(configured.workflowSteps) && configured.workflowSteps.length
      ? configured.workflowSteps
      : workflowSteps({ type, parentName, evidenceRequired, reunderwriteTrigger }),
    derivedFromSavedReport: !Object.keys(configured).length || Boolean(configured.derivedFromSavedReport),
  };
};

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

export const resolvePathRecipients = (company = {}) => {
  const summary = company.summaryData || {};
  const approvers = Array.isArray(company.approvers) ? company.approvers : [];
  const vpRisk = approvers.find((approver) =>
    /vp\s*risk|risk\s*vp|chief\s*risk|head\s*of\s*risk|credit\s*approver/i.test(
      `${approver?.roleName || ""} ${approver?.title || ""}`
    )
  );
  return {
    sales: [
      summary["Sales Owner Email"],
      summary["Account Owner Email"],
      company.salesOwner,
      company.accountOwner,
    ].map(asEmail).find(Boolean) || "",
    vpRisk: asEmail(vpRisk),
    customer: [
      summary["Company Email"],
      summary["Email"],
      company.email,
    ].map(asEmail).find(Boolean) || "",
  };
};

export const buildPathMessage = ({ company, details, action, currentUrl }) => {
  const companyName = company?.name || company?.companyName || "Company";
  const arc = Number(details?.currentArcScore || 0).toFixed(1);
  const currentLimit = money(details?.currentRecommendedLimit);
  const requestedLimit = money(details?.requestedLimit);
  const evidence = Array.isArray(details?.evidenceRequired) ? details.evidenceRequired : [];
  const subjectPrefix = action === "vpRisk"
    ? "VP Risk review requested"
    : action === "documents"
      ? "Documents required"
      : "Next-step plan";
  const roleIntro = action === "vpRisk"
    ? "Please review and approve, reject, or return the proposed path to the requested limit."
    : action === "documents"
      ? "Please provide the evidence below so the credit team can reassess the requested limit."
      : "Please coordinate the next step with the customer and credit team.";
  const evidenceText = evidence.length
    ? evidence.map((item) => `• ${item}`).join("\n")
    : "No additional evidence list was generated.";
  return {
    subject: `${subjectPrefix}: ${companyName}`,
    message: `${roleIntro}

Company: ${companyName}
ARC: ${arc}/10
Current recommended limit: ${currentLimit}
Requested limit: ${requestedLimit}

Recommended path:
${details?.primaryAction || details?.summary || ""}

Required evidence:
${evidenceText}

Re-underwrite when:
${details?.reunderwriteTrigger || ""}
${currentUrl ? `\n\nOpen the Bill.com account assessment: ${currentUrl}` : ""}`,
  };
};

export const buildParentUnderwritingPayload = ({ tenantId, company, details, parentName, parentCountry }) => ({
  tenantId,
  name: String(parentName || "").trim(),
  country: String(parentCountry || "").trim(),
  requestedCreditLimit: Number(details?.requestedLimit) || Number(company?.requestedCreditLimit) || 0,
  view: 1,
  parentCompanyName: "",
  parentCountry: "",
  source: "path_to_requested_limit",
  underwrittenInsteadOfCompanyId: company?.companyId || company?.id || "",
});

export const parentReportPath = ({ tenantId, companyId, currentPath = "" }) =>
  currentPath.startsWith("/tenant-dashboard/")
    ? `/tenant-dashboard/${tenantId}/company/${companyId}`
    : `/company/${companyId}`;

function PathToRequestedLimitCard({ tenantId, company }) {
  const summary = company?.summaryData || {};
  const details = resolvePathDetails(company);
  const recipients = useMemo(() => resolvePathRecipients(company), [company]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [composer, setComposer] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [parentDialogOpen, setParentDialogOpen] = useState(false);
  const [parentName, setParentName] = useState(details?.parentName || "");
  const [parentCountry, setParentCountry] = useState(
    company?.parentCountry ||
    company?.country ||
    summary["Country"] ||
    ""
  );
  const [startingParent, setStartingParent] = useState(false);

  if (tenantId !== BILL_TENANT_ID || !details) return null;

  const openComposer = (action) => {
    const draft = buildPathMessage({
      company,
      details,
      action,
      currentUrl: window.location.href,
    });
    setComposer(action);
    setRecipient(recipients[action] || "");
    setSubject(draft.subject);
    setMessage(draft.message);
    setAnchorEl(null);
  };

  const copyPlan = async () => {
    const evidence = (details.evidenceRequired || []).map((item) => `• ${item}`).join("\n");
    const alternatives = (details.alternativeStructures || [])
      .map((item) => `• ${item.recommendation}`)
      .join("\n");
    await navigator.clipboard.writeText(
      `${details.summary}\n\nEvidence required:\n${evidence}\n\nAlternatives:\n${alternatives}\n\nRe-underwrite when: ${details.reunderwriteTrigger}`
    );
    setAnchorEl(null);
    toast.success("Path to requested limit copied.");
  };

  const openParentUnderwriting = () => {
    setParentName(details?.parentName || "");
    setParentCountry(
      company?.parentCountry ||
      company?.country ||
      summary["Country"] ||
      ""
    );
    setAnchorEl(null);
    setParentDialogOpen(true);
  };

  const underwriteParent = async () => {
    if (!parentName.trim()) {
      toast.error("Enter the parent company's legal name.");
      return;
    }
    if (!parentCountry.trim()) {
      toast.error("Enter the parent company's country.");
      return;
    }

    try {
      setStartingParent(true);
      const payload = buildParentUnderwritingPayload({
        tenantId,
        company,
        details,
        parentName,
        parentCountry,
      });
      const response = await createCompanyFromPlatform(payload);
      const parent = response?.data;
      const parentCompanyId = parent?.id || parent?.companyId;
      if (!parentCompanyId) throw new Error("Parent company was saved without an ID.");

      // New companies are queued by the create endpoint. Existing exact-name
      // companies are updated by that endpoint and need an explicit refresh.
      if (/updated/i.test(response?.message || "")) {
        await startUnderwritingFlow(tenantId, parentCompanyId);
      }

      setParentDialogOpen(false);
      toast.success(`Underwriting started for ${payload.name}.`);
      window.open(
        parentReportPath({
          tenantId,
          companyId: parentCompanyId,
          currentPath: window.location.pathname,
        }),
        "_blank",
        "noopener,noreferrer"
      );
    } catch (error) {
      console.error("Failed to underwrite parent company:", error);
      toast.error(error?.response?.data?.message || error?.message || "Parent underwriting was not started.");
    } finally {
      setStartingParent(false);
    }
  };

  const send = async () => {
    if (!emailPattern.test(recipient.trim())) {
      toast.error("Enter a valid recipient email.");
      return;
    }
    try {
      setSending(true);
      await sendGeneralEmail({
        tenantId,
        to: [recipient.trim()],
        subject: subject.trim(),
        message: `<div>${escapeHtml(message).replace(/\n/g, "<br/>")}</div>`,
        logEventType: composer === "vpRisk" ? "VP Risk Review Request" : "Path to Requested Limit",
        logData: {
          companyId: company.companyId,
          companyName: company.name || company.companyName,
          action: composer,
          arcScore: details.currentArcScore,
          recommendedLimit: details.currentRecommendedLimit,
          requestedLimit: details.requestedLimit,
        },
      });
      toast.success("Message sent.");
      setComposer(null);
    } catch (error) {
      console.error("Failed to send path-to-limit message:", error);
      toast.error("Message was not sent.");
    } finally {
      setSending(false);
    }
  };

  const typeLabel = String(details.type || "next_step")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const limitGap = Math.max(0, Number(details.requestedLimit) - Number(details.currentRecommendedLimit));
  const progress = details.requestedLimit
    ? Math.min(100, (Number(details.currentRecommendedLimit) / Number(details.requestedLimit)) * 100)
    : 0;
  const evidence = Array.isArray(details.evidenceRequired) ? details.evidenceRequired : [];
  const alternatives = Array.isArray(details.alternativeStructures) ? details.alternativeStructures : [];
  const steps = Array.isArray(details.workflowSteps) ? details.workflowSteps : [];
  const evidenceProgress = Array.isArray(details.evidenceProgress) ? details.evidenceProgress : [];
  const structureProgress = Array.isArray(details.structureProgress) ? details.structureProgress : [];
  const evidenceState = (requirement) => evidenceProgress.find((item) => cleanText(item?.requirement).toLowerCase() === cleanText(requirement).toLowerCase());
  const structureState = (structure) => structureProgress.find((item) => {
    const text = typeof structure === "string" ? structure : structure?.recommendation || structure?.description || structure?.summary || structure?.title;
    return cleanText(item?.structure).toLowerCase() === cleanText(text).toLowerCase();
  });
  const satisfiedCount = evidence.filter((item) => evidenceState(item)?.status === "satisfied").length;
  const evidenceComplete = evidence.length > 0 && satisfiedCount === evidence.length;

  return (
    <Paper
      elevation={0}
      sx={{
        mx: 3,
        mt: 2,
        mb: 1,
        p: { xs: 2, md: 2.5 },
        border: "1px solid #DCE4F2",
        borderRadius: "14px",
        background: "#FFFFFF",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
            <Typography sx={{ color: "#1A2A56", fontSize: 16, fontWeight: 700 }}>
              Path to Requested Limit
            </Typography>
            <Chip label={typeLabel} size="small" sx={{ height: 22, fontSize: 11, background: "#E9F0FD", color: "#2F57C4" }} />
            {details.derivedFromSavedReport && (
              <Chip
                label="Built from saved report"
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: 10.5, borderColor: "#D7DEEB", color: "#68738A" }}
              />
            )}
          </Box>
          <Typography sx={{ color: "#3A4358", fontSize: 13.5, lineHeight: 1.6, maxWidth: 980 }}>
            {details.primaryAction || details.summary}
          </Typography>
        </Box>
        <Button
          variant="contained"
          endIcon={<ArrowDropDownIcon />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ textTransform: "none", background: "#2F57C4", whiteSpace: "nowrap" }}
        >
          Run next step
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
          gap: 1,
          mt: 2,
        }}
      >
        {[
          ["Current recommendation", money(details.currentRecommendedLimit)],
          ["Requested limit", money(details.requestedLimit)],
          ["Limit gap", money(limitGap)],
          ["ARC score", `${Number(details.currentArcScore).toFixed(1)}/10`],
        ].map(([label, value]) => (
          <Box
            key={label}
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: "10px",
              border: "1px solid #E4E9F2",
              background: "#F8FAFD",
            }}
          >
            <Typography sx={{ fontSize: 10.5, color: "#748097", textTransform: "uppercase", letterSpacing: 0.45 }}>
              {label}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: 15, color: "#1A2A56", fontWeight: 700 }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 1.25 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.65 }}>
          <Typography sx={{ fontSize: 11.5, color: "#68738A" }}>Current recommendation toward request</Typography>
          <Typography sx={{ fontSize: 11.5, color: "#3A4358", fontWeight: 600 }}>{Math.round(progress)}%</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 7,
            borderRadius: 99,
            backgroundColor: "#E8EDF5",
            "& .MuiLinearProgress-bar": { borderRadius: 99, backgroundColor: "#2F57C4" },
          }}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.08fr) minmax(0, 0.92fr)" },
          gap: 2,
        }}
      >
        <Box sx={{ border: "1px solid #E2E8F2", borderRadius: "12px", p: 1.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1.25 }}>
            <VerifiedUserOutlinedIcon sx={{ fontSize: 19, color: "#2F57C4" }} />
            <Typography sx={{ color: "#1A2A56", fontSize: 13.5, fontWeight: 700 }}>
              Required evidence
            </Typography>
            <Chip label={`${satisfiedCount}/${evidence.length} satisfied`} size="small" sx={{ height: 20, fontSize: 10, ml: "auto", color: evidenceComplete ? "#267349" : "#68738A", background: evidenceComplete ? "#E7F6ED" : "#EEF1F6" }} />
          </Box>
          <Box sx={{ display: "grid", gap: 1 }}>
            {evidence.map((item) => {
              const state = evidenceState(item);
              const status = state?.status || "outstanding";
              const label = status === "not_addressed" ? "Outstanding" : status.replace(/_/g, " ");
              return (
                <Box key={item} sx={{ display: "flex", alignItems: "flex-start", gap: 0.9 }}>
                  {status === "satisfied" ? (
                    <CheckCircleOutlineIcon sx={{ mt: "2px", fontSize: 17, color: "#2E8B57", flex: "0 0 auto" }} />
                  ) : status === "partial" || status === "contradicted" ? (
                    <FactCheckOutlinedIcon sx={{ mt: "2px", fontSize: 17, color: status === "partial" ? "#B7791F" : "#C2413B", flex: "0 0 auto" }} />
                  ) : (
                    <RadioButtonUncheckedIcon sx={{ mt: "2px", fontSize: 16, color: "#9AA6BC", flex: "0 0 auto" }} />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 12.25, lineHeight: 1.45, color: "#46516A" }}>{item}</Typography>
                    {state?.reason && <Typography sx={{ mt: 0.2, fontSize: 10.75, lineHeight: 1.35, color: "#7A8498" }}>{state.reason}</Typography>}
                  </Box>
                  <Chip label={label} size="small" sx={{ height: 19, fontSize: 9.5, textTransform: "capitalize", color: status === "satisfied" ? "#267349" : status === "contradicted" ? "#B42318" : "#68738A", background: status === "satisfied" ? "#E7F6ED" : status === "contradicted" ? "#FDECEA" : "#EEF1F6" }} />
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ border: "1px solid #E2E8F2", borderRadius: "12px", p: 1.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1.25 }}>
            <TrendingUpIcon sx={{ fontSize: 19, color: "#2F57C4" }} />
            <Typography sx={{ color: "#1A2A56", fontSize: 13.5, fontWeight: 700 }}>
              Execution workflow
            </Typography>
          </Box>
          <Box sx={{ display: "grid", gap: 1.15 }}>
            {steps.map((step, index) => (
              <Box key={`${step.title}-${index}`} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                {index === 0 && evidenceComplete ? (
                  <CheckCircleOutlineIcon sx={{ mt: "1px", fontSize: 18, color: "#2E8B57", flex: "0 0 auto" }} />
                ) : (
                  <Box
                    sx={{
                      mt: "1px",
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      border: "1px solid #B5C0D2",
                      color: "#6D7890",
                      fontSize: 10,
                      flex: "0 0 auto",
                    }}
                  >
                    {index + 1}
                  </Box>
                )}
                <Box>
                  <Typography sx={{ fontSize: 12.25, color: "#2B3650", fontWeight: 700 }}>{step.title}</Typography>
                  <Typography sx={{ mt: 0.15, fontSize: 11.5, lineHeight: 1.4, color: "#68738A" }}>
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.08fr) minmax(0, 0.92fr)" },
          gap: 2,
        }}
      >
        <Box sx={{ border: "1px solid #E2E8F2", borderRadius: "12px", p: 1.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1.25 }}>
            <AltRouteOutlinedIcon sx={{ fontSize: 19, color: "#2F57C4" }} />
            <Typography sx={{ color: "#1A2A56", fontSize: 13.5, fontWeight: 700 }}>
              Structures for Risk review
            </Typography>
          </Box>
          <Box sx={{ display: "grid", gap: 1 }}>
            {alternatives.map((item, index) => (
              <Box key={`${item.title}-${index}`} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Chip
                  label={index + 1}
                  size="small"
                  sx={{ mt: "1px", width: 22, height: 22, fontSize: 10, color: "#2F57C4", background: "#EEF3FD" }}
                />
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 12, color: "#2B3650", fontWeight: 700 }}>{item.title}</Typography>
                    {structureState(item)?.status && (
                      <Chip label={structureState(item).status.replace(/_/g, " ")} size="small" sx={{ height: 18, fontSize: 9, textTransform: "capitalize", color: structureState(item).status === "supported" ? "#267349" : "#68738A", background: structureState(item).status === "supported" ? "#E7F6ED" : "#EEF1F6" }} />
                    )}
                  </Box>
                  <Typography sx={{ mt: 0.1, fontSize: 11.5, lineHeight: 1.4, color: "#68738A" }}>
                    {item.recommendation}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: "grid", gap: 1.25 }}>
          <Box sx={{ border: "1px solid #E2E8F2", borderRadius: "12px", p: 1.5 }}>
            <Typography sx={{ fontSize: 10.5, color: "#748097", textTransform: "uppercase", letterSpacing: 0.45 }}>
              Re-underwrite when
            </Typography>
            <Typography sx={{ mt: 0.45, fontSize: 12, lineHeight: 1.5, color: "#3A4358" }}>
              {details.reunderwriteTrigger}
            </Typography>
          </Box>
          <Box sx={{ border: "1px solid #E2E8F2", borderRadius: "12px", p: 1.5 }}>
            <Typography sx={{ fontSize: 10.5, color: "#748097", textTransform: "uppercase", letterSpacing: 0.45 }}>
              Owners and approvals
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.8 }}>
              <Chip
                size="small"
                label={`Sales: ${recipients.sales || "not assigned"}`}
                variant="outlined"
                sx={{ fontSize: 10.5, color: recipients.sales ? "#54617A" : "#A16600" }}
              />
              <Chip
                size="small"
                label={`VP Risk: ${recipients.vpRisk || "not assigned"}`}
                variant="outlined"
                sx={{ fontSize: 10.5, color: recipients.vpRisk ? "#54617A" : "#A16600" }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {(details.type === "parent_support" || details.parentName) && (
          <MenuItem onClick={openParentUnderwriting}>
            <AccountTreeOutlinedIcon sx={{ mr: 1, fontSize: 18 }} /> Underwrite the parent company instead
          </MenuItem>
        )}
        <MenuItem onClick={() => openComposer("sales")}>
          <MailOutlineIcon sx={{ mr: 1, fontSize: 18 }} /> Message Sales
        </MenuItem>
        <MenuItem onClick={() => openComposer("vpRisk")}>
          <FactCheckOutlinedIcon sx={{ mr: 1, fontSize: 18 }} /> Request VP Risk review
        </MenuItem>
        <MenuItem onClick={() => openComposer("documents")}>
          <DescriptionOutlinedIcon sx={{ mr: 1, fontSize: 18 }} /> Request required documents
        </MenuItem>
        <MenuItem onClick={copyPlan}>
          <ContentCopyOutlinedIcon sx={{ mr: 1, fontSize: 18 }} /> Copy full action plan
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(composer)} onClose={() => !sending && setComposer(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {composer === "vpRisk" ? "Request VP Risk review" : composer === "documents" ? "Request documents" : "Message Sales"}
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "12px !important" }}>
          <TextField
            label="Recipient email"
            type="email"
            value={recipient}
            onChange={(event) => setRecipient(event.target.value)}
            helperText={!recipient ? "No role owner is configured for this Bill.com account. Enter or assign the recipient before sending." : ""}
            fullWidth
          />
          <TextField label="Subject" value={subject} onChange={(event) => setSubject(event.target.value)} fullWidth />
          <TextField
            label="Message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            multiline
            minRows={8}
            fullWidth
          />
          <Typography sx={{ fontSize: 11.5, color: "#7A8398" }}>
            Nothing is sent until you click Send. VP Risk review requests do not approve the account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposer(null)} disabled={sending}>Cancel</Button>
          <Button variant="contained" onClick={send} disabled={sending || !emailPattern.test(recipient.trim())}>
            {sending ? "Sending…" : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={parentDialogOpen}
        onClose={() => !startingParent && setParentDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Underwrite the parent company instead</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "12px !important" }}>
          <Typography sx={{ fontSize: 13, color: "#54617A", lineHeight: 1.55 }}>
            This creates or refreshes a separate assessment for the parent at the requested
            30-day credit-card exposure. It does not change {company?.name || company?.companyName}.
          </Typography>
          <TextField
            label="Parent legal name"
            value={parentName}
            onChange={(event) => setParentName(event.target.value)}
            autoFocus
            required
            fullWidth
          />
          <TextField
            label="Parent country"
            value={parentCountry}
            onChange={(event) => setParentCountry(event.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Requested limit"
            value={money(details.requestedLimit)}
            helperText="Copied from the child company's requested limit."
            disabled
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParentDialogOpen(false)} disabled={startingParent}>Cancel</Button>
          <Button
            variant="contained"
            onClick={underwriteParent}
            disabled={startingParent || !parentName.trim() || !parentCountry.trim()}
          >
            {startingParent ? "Starting…" : "Start parent underwriting"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default PathToRequestedLimitCard;
